import React, { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

import invariant from 'tiny-invariant';

import { useGlobalShortcutsDataRelay } from '@atlassian/global-shortcuts/use-global-shortcuts-data-relay';
import GlobalShortcutsRelayClient from '@atlassian/global-shortcuts/utils/GlobalShortcutsRelayClient';
import { useNavBoundaryContext } from '@atlassian/navigation-error-boundary/nav-boundary-context';

import type {
	GlobalSideNavigationProps,
	ItemsConfig,
	NetworkStorageParams,
} from '../../common/types';
import { useOptions } from '../options-provider';
import { useForYouNavItem } from './use-for-you-nav-item';
import { useRecentNavItem } from './use-recent-nav-item';
import { useStarredNavItem } from './use-starred-nav-item';

// Must match the fragment name declared in `useGlobalShortcutsDataRelay`.
const GLOBAL_SHORTCUTS_FRAGMENT_NAME = 'useGlobalShortcutsDataRelay';
// Same value as relay-runtime's exported `FRAGMENTS_KEY`: the key under which Relay stores
// fragment pointers on a fragment reference. Inlined to avoid a runtime dependency on relay-runtime.
const FRAGMENTS_KEY = '__fragments';

export type GlobalItemsConfig = Map<keyof ItemsConfig, ItemsConfig[keyof ItemsConfig]>;

export type ItemsAndShortcuts = Pick<
	GlobalSideNavigationProps,
	'growthItemShortcuts' | 'items' | 'shortcuts'
> & {
	allItems: ItemsConfig;
	forYouMenuId: string;
	// todo: Migrate this to ItemsConfig?
	globalItems: GlobalItemsConfig;
};

export const ItemsAndShortcutsContext: React.Context<ItemsAndShortcuts | undefined> = createContext<
	ItemsAndShortcuts | undefined
>(undefined);

export type ItemsAndShortcutsProviderProps = PropsWithChildren<
	Pick<GlobalSideNavigationProps, 'forYou' | 'growthItemShortcuts' | 'items'> & {
		rawShortcuts: GlobalSideNavigationProps['shortcuts'];
	}
>;

type ItemsAndShortcutsProviderWithQueryRefProps = ItemsAndShortcutsProviderProps & {
	queryRef: NonNullable<NetworkStorageParams['queryRef']>;
};

type ItemsAndShortcutsProviderInnerProps = PropsWithChildren<{
	forYouMenuId: string;
	forYouNavItem: ReturnType<typeof useForYouNavItem>;
	growthItemShortcuts: ItemsAndShortcutsProviderProps['growthItemShortcuts'];
	items: ItemsAndShortcutsProviderProps['items'];
	recentNavItem: ReturnType<typeof useRecentNavItem>;
	shortcuts: GlobalSideNavigationProps['shortcuts'];
	starredNavItem: ReturnType<typeof useStarredNavItem>;
}>;

export function ItemsAndShortcutsProvider(
	props: ItemsAndShortcutsProviderProps,
): React.JSX.Element {
	const { networkStorageParams } = useOptions();
	const queryRef = networkStorageParams?.queryRef;

	if (queryRef && hasGlobalShortcutsFragmentRef(queryRef)) {
		return <ItemsAndShortcutsProviderWithQueryRef {...props} queryRef={queryRef} />;
	}

	// If no queryRef was provided, or the query did not fetch the GAS fragment, fall back to
	// the hydrated version for GAS data.
	return <ItemsAndShortcutsProviderHydrated {...props} />;
}

/**
 * Every query that can produce a `queryRef` spreads `...useGlobalShortcutsDataRelay` with
 * `@include(if: $fetchGlobalShortcutsAvailableProducts)`. When a consumer resolves that variable
 * to `false` (e.g. anonymous users, or no `cloudId`/`ownerAri`), the `queryRef` has no pointer
 * for the fragment and `useFragment` throws an invariant. In that case the GAS data was never
 * fetched, so we must not take the Relay path.
 */
function hasGlobalShortcutsFragmentRef(
	queryRef: NonNullable<NetworkStorageParams['queryRef']>,
): boolean {
	const fragments = (queryRef as { [FRAGMENTS_KEY]?: Record<string, unknown> })[FRAGMENTS_KEY];
	return Boolean(fragments && GLOBAL_SHORTCUTS_FRAGMENT_NAME in fragments);
}

export function useItemsAndShortcuts(): ItemsAndShortcuts {
	const itemsAndShortcuts = useContext(ItemsAndShortcutsContext);

	invariant(
		itemsAndShortcuts,
		'useItemsAndShortcuts must be used inside an initialized ItemsAndShortcutsProvider',
	);

	return itemsAndShortcuts;
}

function ItemsAndShortcutsProviderHydrated({
	children,
	forYou,
	growthItemShortcuts,
	items,
	rawShortcuts,
}: ItemsAndShortcutsProviderProps): React.JSX.Element {
	const { app, cloudId, isShortcutsStatic } = useOptions();
	const { handleError } = useNavBoundaryContext({
		location: 'items-and-shortcuts-provider-agg-hydrated',
		// Log backend errors only to Sentry
		// see: https://hello.atlassian.net/wiki/spaces/navx/pages/6439960649/NavX+-+FE+Observability#%3Acatchemall%3A--Error-handling
		sentryConfig: { product: app },
		disableAnalytics: true,
	});
	const forYouNavItem = useForYouNavItem(forYou);
	const forYouMenuId = forYouNavItem.length ? forYouNavItem[0][0] : '';
	const recentNavItem = useRecentNavItem(forYou);
	const starredNavItem = useStarredNavItem(forYou);

	return (
		<GlobalShortcutsRelayClient
			shortcutsConfig={rawShortcuts}
			cloudId={cloudId}
			isShortcutsStatic={isShortcutsStatic}
			onError={handleError}
			product={app}
		>
			{(shortcuts) => (
				<ItemsAndShortcutsProviderInner
					forYouMenuId={forYouMenuId}
					forYouNavItem={forYouNavItem}
					growthItemShortcuts={growthItemShortcuts}
					items={items}
					recentNavItem={recentNavItem}
					shortcuts={shortcuts}
					starredNavItem={starredNavItem}
				>
					{children}
				</ItemsAndShortcutsProviderInner>
			)}
		</GlobalShortcutsRelayClient>
	);
}

function ItemsAndShortcutsProviderWithQueryRef({
	children,
	forYou,
	growthItemShortcuts,
	items,
	rawShortcuts,
	queryRef,
}: ItemsAndShortcutsProviderWithQueryRefProps): React.JSX.Element {
	const { app, cloudId, isShortcutsStatic } = useOptions();
	const forYouNavItem = useForYouNavItem(forYou);
	const forYouMenuId = forYouNavItem.length ? forYouNavItem[0][0] : '';
	const recentNavItem = useRecentNavItem(forYou);
	const starredNavItem = useStarredNavItem(forYou);
	const { handleError } = useNavBoundaryContext({
		location: 'use-ssr-shortcuts',
		// Log backend errors only to Sentry
		// see: https://hello.atlassian.net/wiki/spaces/navx/pages/6439960649/NavX+-+FE+Observability#%3Acatchemall%3A--Error-handling
		sentryConfig: { product: app },
		disableAnalytics: true,
	});

	const shortcuts = useGlobalShortcutsDataRelay({
		cloudId: cloudId,
		onError: handleError,
		queryRef,
		isShortcutsStatic,
		shortcutsConfig: rawShortcuts,
	});

	return (
		<ItemsAndShortcutsProviderInner
			forYouMenuId={forYouMenuId}
			forYouNavItem={forYouNavItem}
			growthItemShortcuts={growthItemShortcuts}
			items={items}
			recentNavItem={recentNavItem}
			shortcuts={shortcuts}
			starredNavItem={starredNavItem}
		>
			{children}
		</ItemsAndShortcutsProviderInner>
	);
}

function ItemsAndShortcutsProviderInner({
	children,
	forYouMenuId,
	forYouNavItem,
	growthItemShortcuts,
	items,
	recentNavItem,
	shortcuts,
	starredNavItem,
}: ItemsAndShortcutsProviderInnerProps): React.JSX.Element {
	const itemsAndShortcuts = useMemo((): ItemsAndShortcuts => {
		const globalItems: GlobalItemsConfig = new Map([
			...forYouNavItem,
			...recentNavItem,
			...starredNavItem,
		]);
		const allItems: ItemsConfig = { ...Object.fromEntries(globalItems), ...items };
		return {
			allItems,
			forYouMenuId,
			globalItems,
			growthItemShortcuts,
			items,
			shortcuts,
		};
	}, [
		forYouNavItem,
		forYouMenuId,
		recentNavItem,
		starredNavItem,
		items,
		growthItemShortcuts,
		shortcuts,
	]);

	return (
		<ItemsAndShortcutsContext.Provider value={itemsAndShortcuts}>
			{children}
		</ItemsAndShortcutsContext.Provider>
	);
}
